import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  KeyIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import CreateAgentModal from './CreateAgentModal';
import EditAgentModal from './EditAgentModal';
import AgentDetailsModal from './AgentDetailsModal';
import ResetPasswordModal from './ResetPasswordModal';
import PasswordSuccessModal from './PasswordSuccessModal';
import AssignCompaniesModal from './AssignCompaniesModal';

const FieldAgentsPage = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    region: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showPasswordSuccessModal, setShowPasswordSuccessModal] = useState(false);
  const [showCompaniesModal, setShowCompaniesModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    suspended: 0
  });

  useEffect(() => {
    fetchAgents();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await axios.get(`/api/admin/field-agents?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAgents(response.data.agents);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (error) {
      console.error('Error fetching agents:', error);
      alert('שגיאה בטעינת סוכנים');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/field-agents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allAgents = response.data.agents;
      setStats({
        total: allAgents.length,
        active: allAgents.filter(a => a.status === 'active').length,
        inactive: allAgents.filter(a => a.status === 'inactive').length,
        suspended: allAgents.filter(a => a.status === 'suspended').length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateAgent = async (agentData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/admin/field-agents', agentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      fetchAgents();
      fetchStats();
      alert('סוכן נוצר בהצלחה!');
    } catch (error) {
      console.error('Error creating agent:', error);
      alert(error.response?.data?.message || 'שגיאה ביצירת סוכן');
    }
  };

  const handleEditAgent = async (agentData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/admin/field-agents/${selectedAgent.id}`, agentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditModal(false);
      setSelectedAgent(null);
      fetchAgents();
      alert('סוכן עודכן בהצלחה!');
    } catch (error) {
      console.error('Error updating agent:', error);
      alert(error.response?.data?.message || 'שגיאה בעדכון סוכן');
    }
  };

  const handleDeactivateAgent = async (agentId) => {
    if (!window.confirm('האם אתה בטוח שברצונך להשבית סוכן זה?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/admin/field-agents/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAgents();
      fetchStats();
      alert('סוכן הושבת בהצלחה!');
    } catch (error) {
      console.error('Error deactivating agent:', error);
      alert('שגיאה בהשבתת סוכן');
    }
  };

  const handleResetPassword = async (newPassword) => {
    try {
      const token = localStorage.getItem('token');
      
      // Use field agent specific reset password endpoint
      const response = await axios.post(
        `/api/admin/field-agents/${selectedAgent.id}/reset-password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Save the password we set and show success modal
      setGeneratedPassword(response.data.tempPassword);
      setShowResetPasswordModal(false);
      setShowPasswordSuccessModal(true);
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.message || 'שגיאה באיפוס סיסמה');
    }
  };

  const handleChangeStatus = async (agentId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/admin/field-agents/${agentId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchAgents();
      fetchStats();
      alert('סטטוס עודכן בהצלחה!');
    } catch (error) {
      console.error('Error changing status:', error);
      alert('שגיאה בעדכון סטטוס');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, text: 'פעיל' },
      inactive: { color: 'bg-gray-100 text-gray-800', icon: XCircleIcon, text: 'לא פעיל' },
      suspended: { color: 'bg-red-100 text-red-800', icon: ExclamationCircleIcon, text: 'מושעה' }
    };
    const badge = badges[status] || badges.inactive;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {badge.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              ניהול סוכני שטח
            </h1>
            <p className="text-gray-600 mt-1">ניהול וניטור סוכני השטח שלך</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-105 shadow-lg"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            סוכן חדש
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">סה"כ סוכנים</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <UserGroupIcon className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">סוכנים פעילים</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.active}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">לא פעילים</p>
              <p className="text-3xl font-bold text-gray-600 mt-1">{stats.inactive}</p>
            </div>
            <div className="p-3 bg-gray-100 rounded-lg">
              <XCircleIcon className="w-8 h-8 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">מושעים</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{stats.suspended}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <ExclamationCircleIcon className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MagnifyingGlassIcon className="w-4 h-4 inline mr-1" />
              חיפוש
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="שם, אימייל, מזהה עובד..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FunnelIcon className="w-4 h-4 inline mr-1" />
              סטטוס
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">הכל</option>
              <option value="active">פעיל</option>
              <option value="inactive">לא פעיל</option>
              <option value="suspended">מושעה</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              אזור
            </label>
            <input
              type="text"
              value={filters.region}
              onChange={(e) => setFilters({ ...filters, region: e.target.value })}
              placeholder="צפון, דרום, מרכז..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: '', region: '', search: '' })}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              נקה סינונים
            </button>
          </div>
        </div>
      </div>

      {/* Agents Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">לא נמצאו סוכנים</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              צור סוכן ראשון
            </button>
          </div>
        ) : (
          <div className="w-full">
            <table className="w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
                <tr>
                  <th className="w-[25%] px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    סוכן
                  </th>
                  <th className="w-[10%] px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    מזהה עובד
                  </th>
                  <th className="w-[12%] px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    אזור
                  </th>
                  <th className="w-[12%] px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    סטטוס
                  </th>
                  <th className="w-[8%] px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    חברות
                  </th>
                  <th className="w-[8%] px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    יעדים
                  </th>
                  <th className="w-[25%] px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {agents.map((agent) => (
                  <tr key={agent.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs">
                            {agent.user?.first_name?.charAt(0)}{agent.user?.last_name?.charAt(0)}
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-gray-900 truncate" title={`${agent.user?.first_name} ${agent.user?.last_name}`}>
                            {agent.user?.first_name} {agent.user?.last_name}
                          </div>
                          <div className="text-xs text-gray-500 truncate" title={agent.user?.email}>{agent.user?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs font-mono text-gray-900">{agent.employee_id}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-900 truncate block" title={agent.region || '-'}>{agent.region || '-'}</span>
                    </td>
                    <td className="px-3 py-3">
                      {getStatusBadge(agent.status)}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs font-semibold text-gray-900">{agent.stats?.assigned_companies || 0}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs font-semibold text-gray-900">{agent.stats?.active_targets || 0}</span>
                    </td>
                    <td className="px-3 py-3 text-sm font-medium">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <button
                          onClick={() => {
                            setSelectedAgent(agent);
                            setShowDetailsModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="צפה בפרטים"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAgent(agent);
                            setShowEditModal(true);
                          }}
                          className="text-yellow-600 hover:text-yellow-900 p-1"
                          title="ערוך"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAgent(agent);
                            setShowResetPasswordModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-900 p-1"
                          title="אפס סיסמה"
                        >
                          <KeyIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAgent(agent);
                            setShowCompaniesModal(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 p-1"
                          title="הקצה חברות"
                        >
                          <BuildingOfficeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/agent-performance/${agent.id}`)}
                          className="text-green-600 hover:text-green-900 p-1"
                          title="ביצועים"
                        >
                          <ChartBarIcon className="w-4 h-4" />
                        </button>
                        {agent.status === 'active' && (
                          <button
                            onClick={() => handleDeactivateAgent(agent.id)}
                            className="text-red-600 hover:text-red-900"
                            title="השבת"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                הקודם
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.pages}
                className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                הבא
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  מציג <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> עד{' '}
                  <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> מתוך{' '}
                  <span className="font-medium">{pagination.total}</span> תוצאות
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px space-x-reverse" aria-label="Pagination">
                  {[...Array(pagination.pages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pagination.page === i + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateAgentModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateAgent}
        />
      )}

      {showEditModal && selectedAgent && (
        <EditAgentModal
          agent={selectedAgent}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAgent(null);
          }}
          onUpdate={handleEditAgent}
        />
      )}

      {showDetailsModal && selectedAgent && (
        <AgentDetailsModal
          agent={selectedAgent}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedAgent(null);
          }}
          onChangeStatus={handleChangeStatus}
        />
      )}

      {showResetPasswordModal && selectedAgent && (
        <ResetPasswordModal
          agent={selectedAgent}
          onClose={() => {
            setShowResetPasswordModal(false);
            setSelectedAgent(null);
          }}
          onSubmit={handleResetPassword}
        />
      )}

      {showPasswordSuccessModal && selectedAgent && generatedPassword && (
        <PasswordSuccessModal
          agent={selectedAgent}
          password={generatedPassword}
          onClose={() => {
            setShowPasswordSuccessModal(false);
            setSelectedAgent(null);
            setGeneratedPassword(null);
          }}
        />
      )}

      {showCompaniesModal && selectedAgent && (
        <AssignCompaniesModal
          agent={selectedAgent}
          onClose={() => {
            setShowCompaniesModal(false);
            setSelectedAgent(null);
          }}
          onSuccess={() => {
            fetchAgents(); // Refresh agents to update company counts
            setShowCompaniesModal(false);
            setSelectedAgent(null);
          }}
        />
      )}
    </div>
  );
};

export default FieldAgentsPage;

